import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  VStack,
  Text,
  useToast,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, AddIcon, DragHandleIcon } from '@chakra-ui/icons';
import { InfluencerLocation } from '@foodfluence/shared';
import { noSameLocation } from 'utils/locationUtils';
import LocationForm from './LocationForm';
import { Pill } from '../../../components/ui';
import { ff } from '../../../theme';

const LOCATION_ROW_DATA_ATTR = 'data-location-index';

interface LocationsListWithFormProps {
  locations: InfluencerLocation[];
  setLocations: (locations: InfluencerLocation[]) => void;
  /** Called after locations change (e.g. for autosave). If not provided, no callback. */
  onLocationsChange?: (locations: InfluencerLocation[]) => Promise<void>;
  /** Enable drag-and-drop reorder. Default false. */
  enableDragAndDrop?: boolean;
  /** Disable buttons and drag (e.g. while saving). Default false. */
  disabled?: boolean;
  /** Show success/error toasts for add/update/delete. Default false. */
  showToasts?: boolean;
}

const LocationsListWithForm: React.FC<LocationsListWithFormProps> = ({
  locations,
  setLocations,
  onLocationsChange,
  enableDragAndDrop = false,
  disabled = false,
  showToasts = false,
}) => {
  const toast = useToast();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingLocation, setEditingLocation] = useState<InfluencerLocation | undefined>(undefined);
  const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [isDuplicateLocation, setIsDuplicateLocation] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  /** Touch-only: ghost element that follows the finger (location + position) */
  const [touchGhost, setTouchGhost] = useState<{
    location: InfluencerLocation;
    index: number;
    x: number;
    y: number;
  } | null>(null);
  const lastTouchRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const touchDragStartIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (locations.length > 1) {
      setIsDuplicateLocation(!noSameLocation(locations));
    } else {
      setIsDuplicateLocation(false);
    }
  }, [locations]);

  // Document-level touch listeners so we get move/end even when finger leaves the handle
  useEffect(() => {
    if (draggedIndex === null || !enableDragAndDrop) return;

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        if (lastTouchRef.current) {
          lastTouchRef.current = { clientX: x, clientY: y };
        }
        setTouchGhost((prev) => (prev ? { ...prev, x, y } : null));
        e.preventDefault();
      }
    };

    const onTouchEnd = async () => {
      const startIndex = touchDragStartIndexRef.current;
      touchDragStartIndexRef.current = null;
      setTouchGhost(null);
      if (startIndex === null || !lastTouchRef.current) {
        setDraggedIndex(null);
        return;
      }
      const { clientX, clientY } = lastTouchRef.current;
      lastTouchRef.current = null;

      const target = document.elementFromPoint(clientX, clientY);
      const row = target?.closest(`[${LOCATION_ROW_DATA_ATTR}]`);
      const toIndex = row ? parseInt(row.getAttribute(LOCATION_ROW_DATA_ATTR) ?? '-1', 10) : -1;

      if (toIndex >= 0 && toIndex !== startIndex) {
        const newLocations = [...locations];
        const [draggedItem] = newLocations.splice(startIndex, 1);
        newLocations.splice(toIndex, 0, draggedItem);
        setLocations(newLocations);
        if (onLocationsChange) {
          await onLocationsChange(newLocations);
        }
      }
      setDraggedIndex(null);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { once: true });
    document.addEventListener('touchcancel', onTouchEnd, { once: true });

    return () => {
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [draggedIndex, enableDragAndDrop, locations, onLocationsChange]);

  const handleAddLocation = () => {
    setIsFormVisible(true);
    setEditingLocation(undefined);
    setEditingIndex(undefined);
    setIsEditing(false);
  };

  const handleEditLocation = (index: number) => {
    setEditingLocation(locations[index]);
    setEditingIndex(index);
    setIsEditing(true);
    setIsFormVisible(true);
  };

  const handleSaveLocation = async (location: InfluencerLocation, locationIndex?: number) => {
    let newLocations: InfluencerLocation[];
    if (typeof locationIndex === 'number') {
      newLocations = [...locations];
      newLocations[locationIndex] = location;
      if (showToasts) {
        toast({ title: 'Location Updated', status: 'success', duration: 3000, isClosable: true });
      }
    } else {
      newLocations = [...locations, location];
      if (showToasts) {
        toast({ title: 'Location Added', status: 'success', duration: 3000, isClosable: true });
      }
    }
    setLocations(newLocations);
    setIsFormVisible(false);
    setEditingLocation(undefined);
    setEditingIndex(undefined);
    setIsEditing(false);
    if (onLocationsChange) {
      await onLocationsChange(newLocations);
    }
  };

  const handleCancelForm = () => {
    setIsFormVisible(false);
    setEditingLocation(undefined);
    setEditingIndex(undefined);
    setIsEditing(false);
  };

  const handleDeleteLocation = async (index: number) => {
    if (locations.length <= 1) {
      toast({
        title: 'Cannot delete',
        description: 'You must have at least one location',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const newLocations = locations.filter((_, i) => i !== index);
    setLocations(newLocations);
    if (showToasts) {
      toast({ title: 'Location Deleted', status: 'success', duration: 3000, isClosable: true });
    }
    if (onLocationsChange) {
      await onLocationsChange(newLocations);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    const newLocations = [...locations];
    const [draggedItem] = newLocations.splice(draggedIndex, 1);
    newLocations.splice(dropIndex, 0, draggedItem);
    setLocations(newLocations);
    setDraggedIndex(null);
    if (onLocationsChange) {
      await onLocationsChange(newLocations);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleTouchStart = (index: number) => (e: React.TouchEvent) => {
    if (disabled || !enableDragAndDrop) return;
    const x = e.touches[0].clientX;
    const y = e.touches[0].clientY;
    touchDragStartIndexRef.current = index;
    lastTouchRef.current = { clientX: x, clientY: y };
    setTouchGhost({ location: locations[index], index, x, y });
    setDraggedIndex(index);
  };

  return (
    <Box>
      {/* Touch drag ghost: follows finger on mobile */}
      {touchGhost && (
        <Flex
          position="fixed"
          left={touchGhost.x}
          top={touchGhost.y}
          transform="translate(-50%, -50%)"
          alignItems="center"
          border="1px solid"
          borderColor={ff.ink}
          borderRadius="20px"
          p={4}
          bg="white"
          boxShadow="0 8px 24px rgba(14,14,14,0.12)"
          maxW="min(320px, 90vw)"
          zIndex={9999}
          pointerEvents="none"
          transition="none"
        >
          <Flex align="center" mr={3}>
            <DragHandleIcon color={ff.muted} boxSize={4} />
          </Flex>
          <Box mr={3} color={ff.ink}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </Box>
          <VStack align="start" spacing={0} flex="1" minW={0}>
            <HStack spacing={2}>
              <Text fontWeight={600} fontSize="16px" color={ff.ink} noOfLines={1}>
                {touchGhost.location.zipCode || 'No ZIP code'}
              </Text>
              {touchGhost.index === 0 && (
                <Pill tone="ink">Primary</Pill>
              )}
            </HStack>
            <Text fontSize="13px" color={ff.text2} noOfLines={1}>
              {touchGhost.location.city}, {touchGhost.location.state}
            </Text>
          </VStack>
        </Flex>
      )}

      <VStack spacing={2} align="stretch">
        {locations.filter(Boolean).map((location, index) => {
          if (isEditing && editingIndex === index && isFormVisible) {
            return (
              <Box key={`edit-${index}`} border="1px solid" borderColor={ff.line} borderRadius="20px" p={4} bg="white">
                <LocationForm
                  onCancel={handleCancelForm}
                  onSave={handleSaveLocation}
                  locationData={editingLocation}
                  locationIndex={editingIndex}
                  isEditing={isEditing}
                />
              </Box>
            );
          }
          return (
            <Flex
              key={index}
              alignItems="center"
              border="1px solid"
              borderColor={ff.line}
              borderRadius="20px"
              px={4}
              py={3}
              minH="72px"
              bg="white"
              data-location-index={index}
              draggable={enableDragAndDrop && !disabled}
              onDragStart={enableDragAndDrop && !disabled ? () => handleDragStart(index) : undefined}
              onDragOver={enableDragAndDrop ? (e) => handleDragOver(e, index) : undefined}
              onDrop={enableDragAndDrop ? (e) => handleDrop(e, index) : undefined}
              onDragEnd={enableDragAndDrop ? handleDragEnd : undefined}
              opacity={draggedIndex === index ? 0.5 : disabled ? 0.7 : 1}
              cursor={enableDragAndDrop && !disabled ? 'move' : undefined}
              transition="all 0.2s"
            >
              {enableDragAndDrop && (
                <Flex
                  align="center"
                  mr={1}
                  ml={-2}
                  onTouchStart={handleTouchStart(index)}
                  style={{ touchAction: 'none' }}
                >
                  <IconButton
                    icon={<DragHandleIcon />}
                    w="44px"
                    h="44px"
                    minW="44px"
                    borderRadius="full"
                    variant="ghost"
                    aria-label="Drag to reorder"
                    cursor="grab"
                    _active={{ cursor: 'grabbing' }}
                    color={ff.muted}
                  />
                </Flex>
              )}
              <Box mr={3} color={ff.ink}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </Box>
              <VStack align="start" spacing={0} flex="1">
                <HStack spacing={2}>
                  <Text fontWeight={600} fontSize="16px" color={ff.ink}>
                    {location.zipCode || 'No ZIP code'}
                  </Text>
                  {index === 0 && (
                    <Pill tone="ink">Primary</Pill>
                  )}
                </HStack>
                <Text fontSize="13px" color={ff.text2}>
                  {location.city}, {location.state}
                </Text>
              </VStack>
              <HStack spacing={0}>
                <IconButton
                  icon={<EditIcon />}
                  w="44px"
                  h="44px"
                  minW="44px"
                  borderRadius="full"
                  aria-label="Edit Location"
                  variant="ghost"
                  color={ff.ink}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditLocation(index);
                  }}
                  isDisabled={disabled}
                />
                <IconButton
                  icon={<DeleteIcon />}
                  w="44px"
                  h="44px"
                  minW="44px"
                  borderRadius="full"
                  aria-label="Delete Location"
                  variant="ghost"
                  color={ff.danger}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteLocation(index);
                  }}
                  isDisabled={locations.length <= 1 || disabled}
                />
              </HStack>
            </Flex>
          );
        })}
      </VStack>

      {locations.length > 1 && isDuplicateLocation && (
        <Text fontSize="13px" color={ff.danger} bg={ff.cream} px={3} py={2} borderRadius="14px" mt={2}>
          ⚠️ Duplicate location found. Please remove one or edit an existing location.
        </Text>
      )}

      {isFormVisible && !isEditing ? (
        <Box mt={3} border="1px solid" borderColor={ff.line} borderRadius="20px" p={4} bg="white">
          <LocationForm
            onCancel={handleCancelForm}
            onSave={handleSaveLocation}
            locationData={editingLocation}
            locationIndex={editingIndex}
            isEditing={isEditing}
          />
        </Box>
      ) : (
        <Button
          leftIcon={<AddIcon boxSize={3} />}
          variant="inkOutline"
          onClick={handleAddLocation}
          h="48px"
          w="100%"
          mt={3}
          borderStyle="dashed"
          isDisabled={isDuplicateLocation || disabled}
        >
          Add location
        </Button>
      )}
    </Box>
  );
};

export default LocationsListWithForm;
